/**
 * canvas-engine/presets/decompositionAdapters/waas.js
 *
 * WAAS (IT services) family adapter. WAAS sections decompose faithfully with
 * the core engine's vertical-stack layout, so most delegate straight to
 * `buildSectionCanvas`.
 *
 * Family tweak: the two-column "story split", "expertise spotlight", service
 * detail, career hero, and auth sections carry a single foreground image and
 * read best with the image placed beside the lead copy — the shared split hook
 * handles that. Sections with multiple images (image gallery) or no foreground
 * image stay in the default stack.
 *
 * The adapter accepts EITHER a transcribed `WAAS_PRESETS` descriptor OR a
 * section-type string looked up in `WAAS_PRESETS`, laying out at the fixed
 * `Reference_Width` with `idPrefix = sectionType`.
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules + transcribed
 * presetData VALUES. Never emits a `native` element.
 *
 * Requirements: 4.1, 5.1, 5.3, 5.6, 5.7
 */

import { REFERENCE_FRAME_WIDTH } from './buildSectionCanvas';
import { buildFamilyCanvas, wantsSplit } from './familyLayout';
import { resolvePreset, adapterOptions, handledTypesOf } from './resolvePreset';
import { WAAS_PRESETS } from '../presetData/waasPresets';

/** The WAAS section types this adapter handles (keys + sectionTypes). */
export const WAAS_TYPES = Object.freeze(handledTypesOf(WAAS_PRESETS));

/** True when `sectionType` is decomposed by the WAAS adapter. */
export function handlesWaasType(sectionType) {
  return WAAS_TYPES.includes(typeof sectionType === 'string' ? sectionType.trim() : sectionType);
}

/**
 * Decompose a WAAS section into a normalized Canvas_Model.
 *
 * @param {(string|object)} sectionTypeOrData a `WAAS_PRESETS` key / sectionType,
 *   or a transcribed WAAS preset DATA descriptor
 * @param {object} [options] `buildSectionCanvas` options
 * @returns {object} a normalized Canvas_Model
 */
export function waasToCanvas(sectionTypeOrData, options = {}) {
  const { data, sectionType } = resolvePreset(sectionTypeOrData, WAAS_PRESETS, options);
  const opts = adapterOptions(sectionType, REFERENCE_FRAME_WIDTH, options);
  return buildFamilyCanvas(data, opts, { split: wantsSplit(data) });
}

export default waasToCanvas;
